// Date front-fill + fresh label (AMB-371 / F1, module 6).
//
// resolveDueIso fixes a relative phrase to a real ISO date relative to the MEETING (so it's
// stored once and never drifts). friendlyDue renders a fresh label relative to NOW, so an old
// task reads "Overdue · <date>" today even though it was written as "this week".
// All math is in UTC — "YYYY-MM-DD" parses consistently regardless of the runner's timezone.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_MS = 86400000;

const parseISO = (d: string | null | undefined): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || "");
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null;
};
const fmtISO = (dt: Date): string => dt.toISOString().slice(0, 10);
const addDays = (dt: Date, n: number): Date => new Date(dt.getTime() + n * DAY_MS);
const shortDate = (dt: Date): string => `${MONTHS[dt.getUTCMonth()]} ${dt.getUTCDate()}`;

/**
 * Resolve a free-text due into an ISO date, anchored to the meeting date. Returns null when
 * there's no recognizable phrase or no anchor — the caller keeps the raw text in that case.
 */
export function resolveDueIso(freeText: string | null | undefined, meetingDate: string | null | undefined): string | null {
  const text = (freeText || "").toLowerCase().trim();
  if (!text) return null;

  // An explicit ISO date anywhere in the text wins.
  const explicit = /(\d{4}-\d{2}-\d{2})/.exec(text);
  if (explicit) return explicit[1];

  const anchor = parseISO(meetingDate);
  if (!anchor) return null;

  if (/\btoday\b/.test(text)) return fmtISO(anchor);
  if (/\btomorrow\b/.test(text)) return fmtISO(addDays(anchor, 1));
  if (/\bnext week\b/.test(text)) return fmtISO(addDays(anchor, 7));
  if (/\b(this week|end of week|eow|by friday|friday)\b/.test(text)) {
    return fmtISO(addDays(anchor, 5 - anchor.getUTCDay())); // that week's Friday
  }
  return null;
}

/**
 * Format an ISO datetime as a plain date — "Jun 16, 2026". Ignores any time component (the
 * meeting time is a hardcoded 10:00 placeholder until a durable start-time source exists —
 * AMB-356 Facet 2 — so we show the real date, never the fake time).
 */
export function friendlyDate(iso: string | null | undefined): string {
  const dt = parseISO(iso);
  return dt ? `${shortDate(dt)}, ${dt.getUTCFullYear()}` : "";
}

/**
 * A fresh, human label for an ISO date relative to `now` (also ISO). Overdue dates carry the
 * date so they read honestly; near dates collapse to Today / Tomorrow / This week.
 */
export function friendlyDue(iso: string | null | undefined, now: string): string {
  const due = parseISO(iso);
  const today = parseISO(now);
  if (!due || !today) return "";
  const diff = Math.round((due.getTime() - today.getTime()) / DAY_MS);
  if (diff < 0) return `Overdue · ${shortDate(due)}`;
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff <= 7) return "This week";
  return shortDate(due);
}
